import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import styles from "./kpiBuilderCoachMark.module.scss";
import {
  buildSpotlightClipPath,
  computeCoachMarkBalloonStyle,
  COACH_MARK_ANIM_MS,
  measureTargetRect,
} from "./kpiBuilderCoachMarkUtils";

export default function KpiBuilderCoachMark({
  open,
  onClose,
  targetId = "kpi-builder-panel",
}) {
  const { t } = useTranslation();
  const dismissRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [animOpen, setAnimOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const [balloonStyle, setBalloonStyle] = useState({});
  const [placement, setPlacement] = useState("right");

  const updateGeometry = useCallback(() => {
    const target = document.getElementById(targetId);
    const nextRect = measureTargetRect(target);
    setRect(nextRect);
    if (nextRect) {
      const balloon = computeCoachMarkBalloonStyle(nextRect);
      setBalloonStyle({ top: balloon.top, left: balloon.left });
      setPlacement(balloon.placement);
    }
  }, [targetId]);

  useEffect(() => {
    if (!open) {
      setAnimOpen(false);
      const timer = window.setTimeout(() => {
        setMounted(false);
      }, COACH_MARK_ANIM_MS);
      return () => window.clearTimeout(timer);
    }

    setMounted(true);
    updateGeometry();
    const target = document.getElementById(targetId);
    if (target) {
      target.classList.add(styles.panelSpotlight);
    }

    const raf = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setAnimOpen(true));
    });

    const focusTimer = window.setTimeout(() => {
      if (dismissRef.current) {
        dismissRef.current.focus();
      }
    }, COACH_MARK_ANIM_MS);

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(focusTimer);
      if (target) {
        target.classList.remove(styles.panelSpotlight);
      }
    };
  }, [open, targetId, updateGeometry]);

  useEffect(() => {
    if (!mounted) return undefined;

    const handleResizeOrScroll = () => updateGeometry();
    window.addEventListener("resize", handleResizeOrScroll);
    window.addEventListener("scroll", handleResizeOrScroll, true);

    return () => {
      window.removeEventListener("resize", handleResizeOrScroll);
      window.removeEventListener("scroll", handleResizeOrScroll, true);
    };
  }, [mounted, updateGeometry]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (onClose) {
          onClose();
        }
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!mounted || typeof document === "undefined") {
    return null;
  }

  const title = t(
    "team_kpi_view.builderCoachMarkTitle",
    "Create a KPI to add metrics",
  );
  const message = t(
    "team_kpi_view.builderCoachMarkMessage",
    "Select a dashboard from the left panel, add a topic or pivot if needed, then press Create KPI.",
  );
  const dismissLabel = t("team_kpi_view.builderCoachMarkDismiss", "Got it");
  const frameLabel = t("team_kpi_view.builderCoachMarkFrameLabel", "Create KPIs from here");
  const clipPath = buildSpotlightClipPath(rect);
  const framePad = 10;
  const frameStyle = rect
    ? {
        top: rect.top - framePad,
        left: rect.left - framePad,
        width: rect.width + framePad * 2,
        height: rect.height + framePad * 2,
      }
    : null;
  const placementClass =
    styles[`balloonPlacement${placement.charAt(0).toUpperCase()}${placement.slice(1)}`] ||
    "";

  return createPortal(
    <>
      <div
        className={`${styles.overlay} ${animOpen ? styles.overlayOpen : ""}`.trim()}
        style={clipPath ? { clipPath, WebkitClipPath: clipPath } : undefined}
        onClick={() => {
          if (onClose) {
            onClose();
          }
        }}
        aria-hidden="true"
      />
      {frameStyle ? (
        <div
          className={`${styles.spotlightFrame} ${
            animOpen ? styles.spotlightFrameOpen : ""
          }`.trim()}
          style={frameStyle}
          aria-hidden="true"
        >
          <span className={styles.spotlightLabel}>{frameLabel}</span>
        </div>
      ) : null}
      <div
        className={`${styles.balloon} ${placementClass} ${
          animOpen ? styles.balloonOpen : ""
        }`.trim()}
        style={balloonStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="kpi-builder-coach-mark-title"
      >
        <p id="kpi-builder-coach-mark-title" className={styles.balloonTitle}>
          {title}
        </p>
        <p className={styles.balloonMessage}>{message}</p>
        <div aria-live="polite" className={styles.srOnly}>
          {title}. {message}
        </div>
        <button
          ref={dismissRef}
          type="button"
          className={styles.balloonAction}
          onClick={() => {
            if (onClose) {
              onClose();
            }
          }}
        >
          {dismissLabel}
        </button>
      </div>
    </>,
    document.body,
  );
}
